import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "../../styleSheet/main/components/calendar/ReservationsCalendar.scss";
import { createPortal } from "react-dom";
import deleteButtonIcon from "../../media/delete-button.svg";
import { useNotification } from "../NotificationContext";
import Legend from "./Legend";

import { format, parse, startOfWeek, getDay } from "date-fns";
import { pl, enGB } from "date-fns/locale";
import {
  renderRoomReservationDetails,
  renderCarReservationDetails,
} from "./DetailViews";
import { useAuth } from "../../AuthContext";
import Logger from "../../utils/Logger";

const polishMonthsGenitive = [
  "stycznia",
  "lutego",
  "marca",
  "kwietnia",
  "maja",
  "czerwca",
  "lipca",
  "sierpnia",
  "września",
  "października",
  "listopada",
  "grudnia",
];

const polishMonthsNominative = [
  "styczeń",
  "luty",
  "marzec",
  "kwiecień",
  "maj",
  "czerwiec",
  "lipiec",
  "sierpień",
  "wrzesień",
  "październik",
  "listopad",
  "grudzień",
];

// Custom format function for Polish month names
const customFormat = (date, formatStr, options) => {
  // Main calendar header (month view title)
  if (formatStr === "MMMM yyyy") {
    const month = polishMonthsNominative[date.getMonth()];
    const year = date.getFullYear();
    return `${month} ${year}`;
  }
  // Week view date format (dd.MM format)
  if (formatStr === "dd MMMM" || formatStr === "d MMMM") {
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    return `${day}.${month}`;
  }
  // Other potential month formats should use genitive case
  if (formatStr === "MMMM") {
    return polishMonthsGenitive[date.getMonth()];
  }
  return format(date, formatStr, options);
};

const localizer = dateFnsLocalizer({
  format: customFormat,
  parse,
  startOfWeek,
  getDay,
  locales: { "pl-PL": pl },
});

const ReservationsCalendar = ({
  events = [],
  onSelectEvent,
  onSelectSlot,
  height = "80vh",
  defaultView = "month",
  views = ["month", "week", "day"],
  onDateSwitch,
  loading = false,
  error = null,
  legendItems = [], // New prop for legend items
  legendTitle = null, // Optional title for the legend
  showOnlyMine = false, // New prop for "only mine" filter
  onToggleOnlyMine, // Callback for toggling "only mine" filter
  initialDate = new Date(), // Add prop for initial date
}) => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(initialDate);
  const [currentView, setCurrentView] = useState(defaultView);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState([]); // New state for filters

  const { userData } = useAuth();
  // const { showNotification } = useNotification();

  // Filter handling functions
  const handleFilterToggle = (item) => {
    setSelectedFilters((prev) => {
      const isSelected = prev.some(
        (filter) => filter.key === item.key || filter.label === item.label,
      );

      if (isSelected) {
        // Remove filter
        return prev.filter(
          (filter) => filter.key !== item.key && filter.label !== item.label,
        );
      } else {
        // Add filter
        return [...prev, item];
      }
    });
  };

  const handleClearAllFilters = () => {
    setSelectedFilters([]);
  };

  // Filter events based on selected filters
  const filteredEvents = React.useMemo(() => {
    if (selectedFilters.length === 0) {
      return events;
    }

    return events.filter((event) => {
      if (!event.resource) return false;

      // For conference rooms
      if (event.resource.type === "conferenceRoom") {
        return selectedFilters.some(
          (filter) => filter.label === event.resource.conferenceRoomName,
        );
      }

      // For cars
      if (event.resource.type === "car" && event.resource.car) {
        const car = event.resource.car;
        const carLabel = `${car.make} ${car.model} ${car.licensePlate}`;
        return selectedFilters.some((filter) => filter.label === carLabel);
      }

      return false;
    });
  }, [events, selectedFilters]);

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedReservation(null);
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && showDetailModal) {
        closeDetailModal();
      }
    };

    if (showDetailModal) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [showDetailModal]);

  const handleNavigate = (newDate) => {
    const newMonth = newDate.getMonth(); // getMonth() returns 0-11
    const newYear = newDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    setCurrentDate(newDate);
    if (onDateSwitch) {
      if (newMonth !== currentMonth || newYear !== currentYear) {
        Logger.log(`Switching to month: ${newMonth + 1}, year: ${newYear}`);
        onDateSwitch(newDate);
      } else {
        Logger.log("No change in month/year, skipping switch.");
      }
    }
  };

  const showSuccess = (message) => {
    // showNotification(message, "success");
  };

  const notifySuccess = () => {
    showSuccess("Zapisano odpowiedź na spotkanie.");
    onDateSwitch(currentDate);
  };

  const showError = (message) => {
    // showNotification(message, "error");
  };
  const handleSelectEvent = (event) => {
    // Show detail modal if the event has reservation data
    if (event.resource) {
      setSelectedReservation(event.resource);
      setShowDetailModal(true);
    }

    if (onSelectEvent) {
      onSelectEvent(event);
    }
  };

  const handleSelectSlot = (slotInfo) => {
    if (onSelectSlot) {
      onSelectSlot(slotInfo);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    return timeString.slice(0, 5); // Remove seconds, show only HH:MM
  };

  const eventStyleGetter = (event, start, end, isSelected) => {
    const style = {
      backgroundColor: event.resource?.color || "#ff9900", // Use website's orange accent
      borderRadius: "4px",
      opacity: 0.9,
      color: "white",
      border: "0px",
      display: "block",
      fontWeight: "500",
    };

    return { style };
  };

  const messages = {
    allDay: "Cały dzień",
    previous: "Poprzedni",
    next: "Następny",
    today: "Dzisiaj",
    month: "Miesiąc",
    week: "Tydzień",
    day: "Dzień",
    date: "Data",
    time: "Czas",
    event: "Wydarzenie",
    noEventsInRange: "Brak wydarzeń w tym okresie.",
    showMore: (total) => `+ Zobacz więcej (${total})`,
  };

  const { calendarMin, calendarMax, calendarStep } = React.useMemo(() => {
    let minHour = 6;
    let maxHour = 18;

    for (const event of filteredEvents) {
      if (event.start) {
        const h = new Date(event.start).getHours();
        if (h < minHour) minHour = h;
      }
      if (event.end) {
        const end = new Date(event.end);
        const h = end.getHours();
        const m = end.getMinutes();
        const needed = m > 0 ? h + 1 : h;
        if (needed > maxHour) maxHour = Math.min(needed, 24);
      }
    }

    const expanded = minHour < 6 || maxHour > 18;

    return {
      calendarMin: new Date(0, 0, 0, minHour, 0, 0),
      calendarMax: new Date(
        0,
        0,
        0,
        maxHour === 24 ? 23 : maxHour,
        maxHour === 24 ? 59 : 0,
        0,
      ),
      calendarStep: expanded ? 30 : 15,
    };
  }, [filteredEvents]);

  const formats = {
    monthHeaderFormat: (date) => {
      const month = polishMonthsNominative[date.getMonth()];
      const year = date.getFullYear();
      return `${month} ${year}`;
    },
    dayRangeHeaderFormat: ({ start, end }) => {
      const startDay = start.getDate().toString().padStart(2, "0");
      const startMonth = (start.getMonth() + 1).toString().padStart(2, "0");
      const endDay = end.getDate().toString().padStart(2, "0");
      const endMonth = (end.getMonth() + 1).toString().padStart(2, "0");
      return `${startDay}.${startMonth}-${endDay}.${endMonth}`;
    },
  };

  const renderDetailModal = () => {
    if (!showDetailModal || !selectedReservation) return null;

    if (selectedReservation.type === "conferenceRoom") {
      return renderRoomReservationDetails(
        selectedReservation,
        closeDetailModal,
        userData,
        notifySuccess,
        showError, // Pass the error handler function
        navigate,
      );
    } else if (selectedReservation.type === "car") {
      return createPortal(
        renderCarReservationDetails(
          selectedReservation,
          closeDetailModal,
          userData,
          notifySuccess,
          showError,
          navigate,
        ),
        document.body,
      );
    }
  };

  return (
    <>
      <div className="reservations-calendar-container" style={{ height }}>
        {loading ? (
          <div className="calendar-loading">Ładowanie kalendarza...</div>
        ) : error ? (
          <div className="calendar-error">Błąd: {error}</div>
        ) : (
          <>
            <Calendar
              localizer={localizer}
              events={filteredEvents}
              startAccessor="start"
              endAccessor="end"
              style={{ height: "100%" }}
              onSelectEvent={handleSelectEvent}
              onSelectSlot={handleSelectSlot}
              onNavigate={handleNavigate}
              onView={(view) => setCurrentView(view)}
              date={currentDate}
              view={currentView}
              selectable
              eventPropGetter={eventStyleGetter}
              views={views}
              messages={messages}
              formats={formats}
              popup
              step={calendarStep}
              min={calendarMin}
              max={calendarMax}
              showMultiDayTimes
              culture="pl-PL"
            />
            <Legend
              items={legendItems}
              title={legendTitle}
              selectedFilters={selectedFilters}
              onFilterToggle={handleFilterToggle}
              showOnlyMine={showOnlyMine}
              onToggleOnlyMine={onToggleOnlyMine}
            />
          </>
        )}
      </div>
      {renderDetailModal()}
    </>
  );
};

export default ReservationsCalendar;
